import React, { useState, useEffect } from 'react';
import Notification from '../Notification';

const ContentManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [pageContent, setPageContent] = useState({
    home: {
      title: 'RiverPrime - Premier Forex & CFD Trading',
      metaDescription: 'Trade Forex, Indices, Commodities, and Shares with RiverPrime. Advanced trading platforms, competitive spreads, and professional support.',
      heroTitle: 'Trade Forex, Shares, Indices, Commodities',
      heroSubtitle: 'with a Financial Mindset',
      heroDescription: 'Access 2000 instruments across asset classes to trade, hedge and invest from a single account.',
      
      // Trading Steps Section
      stepsTitle: 'Simple steps to start trading',
      step1Title: 'Register account',
      step1Description: 'Create your trading account in minutes with our simple registration process.',
      step2Title: 'Fund your account', 
      step2Description: 'Deposit funds securely using various payment methods including bank transfer and e-wallets.',
      step3Title: 'Start your trade',
      step3Description: 'Access our award-winning platform and start trading across multiple asset classes.',
      stepsButton1: 'Get Started Today',
      stepsButton2: 'Learn More',
      
      // Services Section
      servicesTitle: 'Get Industry-Leading Prices',
      servicesSubtitle: 'Find spreads as low as 0.0 pips for Forex and stock trading using our powerful platforms.',
      
      forexTitle: 'Forex',
      forexPriceLabel: 'Spread as low as',
      forexPriceValue: '0.0 pip',
      forexDescription: 'Trade 200+ Forex spot pairs and 200+ futures across majors and minors with a handful of instruments.',
      
      indicesTitle: 'Indices',
      indicesPriceLabel: 'Commission as low as',
      indicesPriceValue: '0 $',
      indicesDescription: 'Trade on the world\'s largest stock marketplaces using the most powerful tools available.',
      
      sharesTitle: 'Shares',
      sharesPriceLabel: 'Commission as low as',
      sharesPriceValue: '0 $',
      sharesDescription: 'Enjoy leveraged commission-free on trading. Get access to 1000+ CFD stocks. Go long or short easily.',
      
      commoditiesTitle: 'Commodities',
      commoditiesPriceLabel: 'Spread as low as',
      commoditiesPriceValue: '0.004 pip',
      commoditiesDescription: 'Get ahead by trading on energy and metals. Drive the global economy.',
      
      servicesCtaText: 'Start your financial trading journey now.',
      servicesCtaButton: 'Create Account'
    },
    platforms: {
      title: 'Trading Platforms | RiverPrime',
      metaDescription: 'Discover our advanced trading platforms including MetaTrader 5 with cutting-edge features for professional trading.',
      heroTitle: 'Advanced Trading Platforms',
      heroSubtitle: 'Professional trading tools designed for success',
      heroDescription: 'Experience next-generation trading with our sophisticated platform suite.'
    },
    accounts: {
      title: 'Trading Accounts | RiverPrime',
      metaDescription: 'Choose from our range of trading accounts - Pro, Prime, and Premium. Find the perfect account for your trading style.',
      heroTitle: 'Trading Accounts',
      heroSubtitle: 'Choose the account that matches your trading style',
      heroDescription: 'From beginners to professionals, we have an account for every trader.'
    },
    markets: {
      title: 'Markets & Instruments | RiverPrime',
      metaDescription: 'Trade 500+ instruments including Forex, Indices, Commodities, and Shares with competitive spreads and fast execution.',
      heroTitle: 'Global Markets',
      heroSubtitle: 'Access 500+ trading instruments',
      heroDescription: 'Trade the world\'s most popular markets with competitive conditions.'
    },
    about: {
      title: 'About RiverPrime | Leading Forex Broker',
      metaDescription: 'Learn about RiverPrime\'s mission, values, regulations, and commitment to providing exceptional trading services.',
      heroTitle: 'About RiverPrime',
      heroSubtitle: 'Your trusted trading partner',
      heroDescription: 'Committed to excellence in trading services since our inception.'
    },
    partnership: {
      title: 'Partnership Program | RiverPrime',
      metaDescription: 'Join our Introducing Broker program and earn competitive commissions while growing your trading business with RiverPrime.',
      heroTitle: 'Partnership Program',
      heroSubtitle: 'Grow your business with us',
      heroDescription: 'Earn competitive commissions as an Introducing Broker partner.'
    }
  });

  const [siteSettings, setSiteSettings] = useState({
    siteName: 'RiverPrime',
    tagline: 'Your Gateway to Global Markets',
    defaultMetaDescription: 'RiverPrime - Premier Forex and CFD trading with advanced platforms, competitive spreads, and professional support.',
    contactEmail: 'support@riverprime.com',
    contactPhone: '+1 (555) 123-4567',
    companyAddress: '123 Financial District, Trading City, TC 12345',
    tradingHours: 'Monday to Friday, 24/5 Market Hours',
    riskWarning: 'CFDs are complex instruments and come with a high risk of losing money rapidly due to leverage.'
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    const savedContent = localStorage.getItem('adminPageContent');
    const savedSettings = localStorage.getItem('adminSiteSettings');
    
    if (savedContent) {
      setPageContent(JSON.parse(savedContent));
    }
    if (savedSettings) {
      setSiteSettings(JSON.parse(savedSettings));
    }
  }, []);

  const handleContentChange = (page, field, value) => {
    setPageContent(prev => ({
      ...prev,
      [page]: {
        ...prev[page],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSettingChange = (field, value) => {
    setSiteSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    localStorage.setItem('adminPageContent', JSON.stringify(pageContent));
    localStorage.setItem('adminSiteSettings', JSON.stringify(siteSettings));
    setHasChanges(false);
    
    // Trigger content update event for other components
    window.dispatchEvent(new CustomEvent('contentUpdated', { 
      detail: { pageContent, siteSettings } 
    }));
    
    // Show success notification
    setNotification({
      message: 'Content settings saved successfully! Changes are now live on the website.',
      type: 'success'
    });
  };

  const pages = [
    { key: 'home', title: 'Home Page', icon: 'fas fa-home', description: 'Main landing page content and hero section' },
    { key: 'platforms', title: 'Platforms', icon: 'fas fa-desktop', description: 'Trading platforms and tools information' },
    { key: 'accounts', title: 'Accounts', icon: 'fas fa-user-circle', description: 'Account types and trading conditions' },
    { key: 'markets', title: 'Markets', icon: 'fas fa-chart-line', description: 'Available markets and instruments' },
    { key: 'about', title: 'About Us', icon: 'fas fa-info-circle', description: 'Company information and values' },
    { key: 'partnership', title: 'Partnership', icon: 'fas fa-handshake', description: 'Partnership and IB program details' }
  ];

  const renderPageEditor = (pageKey) => {
    const page = pages.find(p => p.key === pageKey);
    if (!page) return null;

    return (
      <div style={{ padding: '30px' }}>
        <div className="page-editor" style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '30px',
          border: '2px solid #e2e8f0',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div className="page-editor-header" style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '30px',
            paddingBottom: '20px',
            borderBottom: '2px solid #f7fafc'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              background: 'linear-gradient(135deg, #2652a0 0%, #1a4086 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '20px',
              boxShadow: '0 4px 12px rgba(38, 82, 160, 0.3)'
            }}>
              <i className={page.icon} style={{ 
                color: '#ffdd00',
                fontSize: '24px'
              }}></i>
            </div>
            <div>
              <h2 style={{
                margin: '0 0 8px',
                color: '#1a202c',
                fontSize: '1.8rem',
                fontWeight: '700'
              }}>{page.title}</h2>
              <p style={{
                color: '#4a5568',
                margin: '0',
                fontSize: '15px',
                fontWeight: '500'
              }}>{page.description}</p>
              <span style={{
                color: '#3182ce',
                fontSize: '12px',
                textTransform: 'uppercase',
                fontWeight: '600',
                background: '#ebf8ff',
                padding: '4px 8px',
                borderRadius: '4px',
                display: 'inline-block',
                marginTop: '8px'
              }}>/{pageKey}</span>
            </div>
          </div>

          <div className="editor-sections" style={{ display: 'grid', gap: '30px' }}>
            {/* SEO Section */}
            <div className="editor-section" style={{
              background: '#f7fafc',
              borderRadius: '12px',
              padding: '25px',
              border: '2px solid #e2e8f0'
            }}>
              <h3 style={{
                color: '#2d3748',
                marginBottom: '20px',
                fontSize: '1.3rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center'
              }}>
                <i className="fas fa-search" style={{ marginRight: '10px', color: '#38a169' }}></i>
                SEO & Meta Information
              </h3>
              
              <div className="form-grid" style={{
                display: 'grid',
                gap: '25px'
              }}>
                <div className="form-group">
                  <label style={{
                    display: 'block',
                    marginBottom: '10px',
                    fontWeight: '600',
                    color: '#2d3748',
                    fontSize: '15px'
                  }}>
                    <i className="fas fa-tag" style={{ marginRight: '8px', color: '#3182ce' }}></i>
                    Page Title (Browser Tab & SEO)
                  </label>
                  <input
                    type="text"
                    value={pageContent[pageKey].title}
                    onChange={(e) => handleContentChange(pageKey, 'title', e.target.value)}
                    placeholder="Enter SEO-optimized page title"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '500',
                      color: '#2d3748',
                      transition: 'all 0.3s ease',
                      background: '#ffffff',
                      outline: 'none',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3182ce'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                  <small style={{
                    color: '#718096',
                    fontSize: '13px',
                    display: 'block',
                    marginTop: '8px',
                    fontWeight: '500'
                  }}>
                    Recommended: 50-60 characters for optimal SEO
                  </small>
                </div>

                <div className="form-group">
                  <label style={{
                    display: 'block',
                    marginBottom: '10px',
                    fontWeight: '600',
                    color: '#2d3748',
                    fontSize: '15px'
                  }}>
                    <i className="fas fa-file-text" style={{ marginRight: '8px', color: '#3182ce' }}></i>
                    Meta Description
                  </label>
                  <textarea
                    value={pageContent[pageKey].metaDescription}
                    onChange={(e) => handleContentChange(pageKey, 'metaDescription', e.target.value)}
                    placeholder="Write a compelling description for search engines..."
                    rows="4"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '500',
                      color: '#2d3748',
                      resize: 'vertical',
                      background: '#ffffff',
                      outline: 'none',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      lineHeight: '1.5'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3182ce'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', alignItems: 'center' }}>
                    <small style={{ 
                      color: pageContent[pageKey].metaDescription.length > 160 ? '#e53e3e' : 
                             pageContent[pageKey].metaDescription.length < 120 ? '#d69e2e' : '#38a169',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}>
                      {pageContent[pageKey].metaDescription.length}/160 characters
                      {pageContent[pageKey].metaDescription.length > 160 && ' - Too long!'}
                      {pageContent[pageKey].metaDescription.length < 120 && pageContent[pageKey].metaDescription.length > 0 && ' - Consider adding more'}
                      {pageContent[pageKey].metaDescription.length >= 120 && pageContent[pageKey].metaDescription.length <= 160 && ' - Perfect length!'}
                    </small>
                    <small style={{ color: '#718096', fontSize: '12px', fontWeight: '500' }}>
                      Optimal: 120-160 characters
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Content Section */}
            <div className="editor-section" style={{
              background: '#f7fafc',
              borderRadius: '12px',
              padding: '25px',
              border: '2px solid #e2e8f0'
            }}>
              <h3 style={{
                color: '#2d3748',
                marginBottom: '20px',
                fontSize: '1.3rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center'
              }}>
                <i className="fas fa-image" style={{ marginRight: '10px', color: '#ed8936' }}></i>
                Hero Section Content
              </h3>
              
              <div className="form-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '25px'
              }}>
                <div className="form-group">
                  <label style={{
                    display: 'block',
                    marginBottom: '10px',
                    fontWeight: '600',
                    color: '#2d3748',
                    fontSize: '15px'
                  }}>
                    <i className="fas fa-heading" style={{ marginRight: '8px', color: '#3182ce' }}></i>
                    Main Heading
                  </label>
                  <input
                    type="text"
                    value={pageContent[pageKey].heroTitle}
                    onChange={(e) => handleContentChange(pageKey, 'heroTitle', e.target.value)}
                    placeholder="Compelling main headline"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '500',
                      color: '#2d3748',
                      background: '#ffffff',
                      outline: 'none',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3182ce'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                <div className="form-group">
                  <label style={{
                    display: 'block',
                    marginBottom: '10px',
                    fontWeight: '600',
                    color: '#2d3748',
                    fontSize: '15px'
                  }}>
                    <i className="fas fa-text-height" style={{ marginRight: '8px', color: '#3182ce' }}></i>
                    Subtitle/Highlight
                  </label>
                  <input
                    type="text"
                    value={pageContent[pageKey].heroSubtitle}
                    onChange={(e) => handleContentChange(pageKey, 'heroSubtitle', e.target.value)}
                    placeholder="Supporting subtitle or highlight text"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '500',
                      color: '#2d3748',
                      background: '#ffffff',
                      outline: 'none',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3182ce'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '10px',
                    fontWeight: '600',
                    color: '#2d3748',
                    fontSize: '15px'
                  }}>
                    <i className="fas fa-align-left" style={{ marginRight: '8px', color: '#3182ce' }}></i>
                    Description Text
                  </label>
                  <textarea
                    value={pageContent[pageKey].heroDescription}
                    onChange={(e) => handleContentChange(pageKey, 'heroDescription', e.target.value)}
                    placeholder="Detailed description for the hero section..."
                    rows="4"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '500',
                      color: '#2d3748',
                      resize: 'vertical',
                      background: '#ffffff',
                      outline: 'none',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      lineHeight: '1.5'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3182ce'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
              </div>
            </div>

            {/* Trading Steps Section - Only for Home page */}
            {pageKey === 'home' && (
              <div className="editor-section" style={{
                background: '#f7fafc',
                borderRadius: '12px',
                padding: '25px',
                border: '2px solid #e2e8f0'
              }}>
                <h3 style={{
                  color: '#2d3748',
                  marginBottom: '20px',
                  fontSize: '1.3rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <i className="fas fa-list-ol" style={{ marginRight: '10px', color: '#805ad5' }}></i>
                  Trading Steps Section
                </h3>
                
                <div className="form-grid" style={{
                  display: 'grid',
                  gap: '20px'
                }}>
                  <div className="form-group">
                    <label style={{
                      display: 'block',
                      marginBottom: '10px',
                      fontWeight: '600',
                      color: '#2d3748',
                      fontSize: '15px'
                    }}>
                      <i className="fas fa-heading" style={{ marginRight: '8px', color: '#3182ce' }}></i>
                      Section Title
                    </label>
                    <input
                      type="text"
                      value={pageContent[pageKey].stepsTitle || ''}
                      onChange={(e) => handleContentChange(pageKey, 'stepsTitle', e.target.value)}
                      placeholder="Simple steps to start trading"
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontWeight: '500',
                        color: '#2d3748',
                        background: '#ffffff',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3182ce'}
                      onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                    />
                  </div>

                  <div className="steps-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '20px'
                  }}>
                    {/* Step 1 */}
                    <div className="step-group" style={{
                      background: '#ffffff',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <h4 style={{ color: '#2d3748', marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>
                        <i className="fas fa-user-plus" style={{ marginRight: '8px', color: '#38a169' }}></i>
                        Step 1
                      </h4>
                      <div className="form-group" style={{ marginBottom: '15px' }}>
                        <label style={{
                          display: 'block',
                          marginBottom: '5px',
                          fontWeight: '600',
                          color: '#4a5568',
                          fontSize: '14px'
                        }}>Title</label>
                        <input
                          type="text"
                          value={pageContent[pageKey].step1Title || ''}
                          onChange={(e) => handleContentChange(pageKey, 'step1Title', e.target.value)}
                          placeholder="Register account"
                          style={{
                            width: '100%',
                            padding: '12px 14px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: '#2d3748'
                          }}
                        />
                      </div>
                      <div className="form-group">
                        <label style={{
                          display: 'block',
                          marginBottom: '5px',
                          fontWeight: '600',
                          color: '#4a5568',
                          fontSize: '14px'
                        }}>Description</label>
                        <textarea
                          value={pageContent[pageKey].step1Description || ''}
                          onChange={(e) => handleContentChange(pageKey, 'step1Description', e.target.value)}
                          placeholder="Create your trading account..."
                          rows="3"
                          style={{
                            width: '100%',
                            padding: '12px 14px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: '#2d3748',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="step-group" style={{
                      background: '#ffffff',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <h4 style={{ color: '#2d3748', marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>
                        <i className="fas fa-coins" style={{ marginRight: '8px', color: '#ed8936' }}></i>
                        Step 2
                      </h4>
                      <div className="form-group" style={{ marginBottom: '15px' }}>
                        <label style={{
                          display: 'block',
                          marginBottom: '5px',
                          fontWeight: '600',
                          color: '#4a5568',
                          fontSize: '14px'
                        }}>Title</label>
                        <input
                          type="text"
                          value={pageContent[pageKey].step2Title || ''}
                          onChange={(e) => handleContentChange(pageKey, 'step2Title', e.target.value)}
                          placeholder="Fund your account"
                          style={{
                            width: '100%',
                            padding: '12px 14px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: '#2d3748'
                          }}
                        />
                      </div>
                      <div className="form-group">
                        <label style={{
                          display: 'block',
                          marginBottom: '5px',
                          fontWeight: '600',
                          color: '#4a5568',
                          fontSize: '14px'
                        }}>Description</label>
                        <textarea
                          value={pageContent[pageKey].step2Description || ''}
                          onChange={(e) => handleContentChange(pageKey, 'step2Description', e.target.value)}
                          placeholder="Deposit funds securely..."
                          rows="3"
                          style={{
                            width: '100%',
                            padding: '12px 14px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: '#2d3748',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div className="step-group" style={{
                      background: '#ffffff',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <h4 style={{ color: '#2d3748', marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>
                        <i className="fas fa-chart-line" style={{ marginRight: '8px', color: '#3182ce' }}></i>
                        Step 3
                      </h4>
                      <div className="form-group" style={{ marginBottom: '15px' }}>
                        <label style={{
                          display: 'block',
                          marginBottom: '5px',
                          fontWeight: '600',
                          color: '#4a5568',
                          fontSize: '14px'
                        }}>Title</label>
                        <input
                          type="text"
                          value={pageContent[pageKey].step3Title || ''}
                          onChange={(e) => handleContentChange(pageKey, 'step3Title', e.target.value)}
                          placeholder="Start your trade"
                          style={{
                            width: '100%',
                            padding: '12px 14px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: '#2d3748'
                          }}
                        />
                      </div>
                      <div className="form-group">
                        <label style={{
                          display: 'block',
                          marginBottom: '5px',
                          fontWeight: '600',
                          color: '#4a5568',
                          fontSize: '14px'
                        }}>Description</label>
                        <textarea
                          value={pageContent[pageKey].step3Description || ''}
                          onChange={(e) => handleContentChange(pageKey, 'step3Description', e.target.value)}
                          placeholder="Access our award-winning platform..."
                          rows="3"
                          style={{
                            width: '100%',
                            padding: '12px 14px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: '#2d3748',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="buttons-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '15px'
                  }}>
                    <div className="form-group">
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '600',
                        color: '#2d3748',
                        fontSize: '15px'
                      }}>Primary Button Text</label>
                      <input
                        type="text"
                        value={pageContent[pageKey].stepsButton1 || ''}
                        onChange={(e) => handleContentChange(pageKey, 'stepsButton1', e.target.value)}
                        placeholder="Get Started Today"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          border: '2px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '15px',
                          fontWeight: '500',
                          color: '#2d3748',
                          background: '#ffffff',
                          outline: 'none'
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '600',
                        color: '#2d3748',
                        fontSize: '15px'
                      }}>Secondary Button Text</label>
                      <input
                        type="text"
                        value={pageContent[pageKey].stepsButton2 || ''}
                        onChange={(e) => handleContentChange(pageKey, 'stepsButton2', e.target.value)}
                        placeholder="Learn More"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          border: '2px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '15px',
                          fontWeight: '500',
                          color: '#2d3748',
                          background: '#ffffff',
                          outline: 'none'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Services Section - Only for Home page */}
            {pageKey === 'home' && (
              <div className="editor-section" style={{
                background: '#f7fafc',
                borderRadius: '12px',
                padding: '25px',
                border: '2px solid #e2e8f0'
              }}>
                <h3 style={{
                  color: '#2d3748',
                  marginBottom: '20px',
                  fontSize: '1.3rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <i className="fas fa-chart-bar" style={{ marginRight: '10px', color: '#38a169' }}></i>
                  Services/Pricing Section
                </h3>
                
                <div className="form-grid" style={{
                  display: 'grid',
                  gap: '25px'
                }}>
                  {/* Section Header */}
                  <div className="section-header" style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <h4 style={{ color: '#2d3748', marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>
                      Section Header
                    </h4>
                    <div className="form-group" style={{ marginBottom: '15px' }}>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '600',
                        color: '#4a5568',
                        fontSize: '14px'
                      }}>Main Title</label>
                      <input
                        type="text"
                        value={pageContent[pageKey].servicesTitle || ''}
                        onChange={(e) => handleContentChange(pageKey, 'servicesTitle', e.target.value)}
                        placeholder="Get Industry-Leading Prices"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          border: '2px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '15px',
                          fontWeight: '500',
                          color: '#2d3748',
                          background: '#ffffff',
                          outline: 'none'
                        }}
                      />
                    </div>
                    <div className="form-group">
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: '600',
                        color: '#4a5568',
                        fontSize: '14px'
                      }}>Subtitle</label>
                      <textarea
                        value={pageContent[pageKey].servicesSubtitle || ''}
                        onChange={(e) => handleContentChange(pageKey, 'servicesSubtitle', e.target.value)}
                        placeholder="Find spreads as low as 0.0 pips for Forex and stock trading..."
                        rows="2"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          border: '2px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '15px',
                          fontWeight: '500',
                          color: '#2d3748',
                          background: '#ffffff',
                          outline: 'none',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                  </div>

                  {/* Service Cards */}
                  <div className="services-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '20px'
                  }}>
                    {/* Forex Card */}
                    <div className="service-card" style={{
                      background: '#ffffff',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '2px solid #ffd700',
                      borderLeft: '5px solid #ffd700'
                    }}>
                      <h5 style={{ color: '#2d3748', marginBottom: '15px', fontSize: '15px', fontWeight: '700' }}>
                        💰 Forex Service
                      </h5>
                      <div style={{ display: 'grid', gap: '12px' }}>
                        <input
                          type="text"
                          value={pageContent[pageKey].forexTitle || ''}
                          onChange={(e) => handleContentChange(pageKey, 'forexTitle', e.target.value)}
                          placeholder="Forex"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#2d3748'
                          }}
                        />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                          <input
                            type="text"
                            value={pageContent[pageKey].forexPriceLabel || ''}
                            onChange={(e) => handleContentChange(pageKey, 'forexPriceLabel', e.target.value)}
                            placeholder="Spread as low as"
                            style={{
                              width: '100%',
                              padding: '8px 10px',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              fontSize: '13px',
                              color: '#2d3748'
                            }}
                          />
                          <input
                            type="text"
                            value={pageContent[pageKey].forexPriceValue || ''}
                            onChange={(e) => handleContentChange(pageKey, 'forexPriceValue', e.target.value)}
                            placeholder="0.0 pip"
                            style={{
                              width: '100%',
                              padding: '8px 10px',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: '700',
                              color: '#2d3748'
                            }}
                          />
                        </div>
                        <textarea
                          value={pageContent[pageKey].forexDescription || ''}
                          onChange={(e) => handleContentChange(pageKey, 'forexDescription', e.target.value)}
                          placeholder="Trade 200+ Forex spot pairs..."
                          rows="3"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '13px',
                            color: '#2d3748',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    </div>

                    {/* Indices Card */}
                    <div className="service-card" style={{
                      background: '#ffffff',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '2px solid #4299e1',
                      borderLeft: '5px solid #4299e1'
                    }}>
                      <h5 style={{ color: '#2d3748', marginBottom: '15px', fontSize: '15px', fontWeight: '700' }}>
                        📈 Indices Service
                      </h5>
                      <div style={{ display: 'grid', gap: '12px' }}>
                        <input
                          type="text"
                          value={pageContent[pageKey].indicesTitle || ''}
                          onChange={(e) => handleContentChange(pageKey, 'indicesTitle', e.target.value)}
                          placeholder="Indices"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#2d3748'
                          }}
                        />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                          <input
                            type="text"
                            value={pageContent[pageKey].indicesPriceLabel || ''}
                            onChange={(e) => handleContentChange(pageKey, 'indicesPriceLabel', e.target.value)}
                            placeholder="Commission as low as"
                            style={{
                              width: '100%',
                              padding: '8px 10px',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              fontSize: '13px',
                              color: '#2d3748'
                            }}
                          />
                          <input
                            type="text"
                            value={pageContent[pageKey].indicesPriceValue || ''}
                            onChange={(e) => handleContentChange(pageKey, 'indicesPriceValue', e.target.value)}
                            placeholder="0 $"
                            style={{
                              width: '100%',
                              padding: '8px 10px',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: '700',
                              color: '#2d3748'
                            }}
                          />
                        </div>
                        <textarea
                          value={pageContent[pageKey].indicesDescription || ''}
                          onChange={(e) => handleContentChange(pageKey, 'indicesDescription', e.target.value)}
                          placeholder="Trade on the world's largest stock marketplaces..."
                          rows="3"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '13px',
                            color: '#2d3748',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    </div>

                    {/* Shares Card */}
                    <div className="service-card" style={{
                      background: '#ffffff',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '2px solid #48bb78',
                      borderLeft: '5px solid #48bb78'
                    }}>
                      <h5 style={{ color: '#2d3748', marginBottom: '15px', fontSize: '15px', fontWeight: '700' }}>
                        📊 Shares Service
                      </h5>
                      <div style={{ display: 'grid', gap: '12px' }}>
                        <input
                          type="text"
                          value={pageContent[pageKey].sharesTitle || ''}
                          onChange={(e) => handleContentChange(pageKey, 'sharesTitle', e.target.value)}
                          placeholder="Shares"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#2d3748'
                          }}
                        />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                          <input
                            type="text"
                            value={pageContent[pageKey].sharesPriceLabel || ''}
                            onChange={(e) => handleContentChange(pageKey, 'sharesPriceLabel', e.target.value)}
                            placeholder="Commission as low as"
                            style={{
                              width: '100%',
                              padding: '8px 10px',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              fontSize: '13px',
                              color: '#2d3748'
                            }}
                          />
                          <input
                            type="text"
                            value={pageContent[pageKey].sharesPriceValue || ''}
                            onChange={(e) => handleContentChange(pageKey, 'sharesPriceValue', e.target.value)}
                            placeholder="0 $"
                            style={{
                              width: '100%',
                              padding: '8px 10px',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: '700',
                              color: '#2d3748'
                            }}
                          />
                        </div>
                        <textarea
                          value={pageContent[pageKey].sharesDescription || ''}
                          onChange={(e) => handleContentChange(pageKey, 'sharesDescription', e.target.value)}
                          placeholder="Enjoy leveraged commission-free on trading..."
                          rows="3"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '13px',
                            color: '#2d3748',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    </div>

                    {/* Commodities Card */}
                    <div className="service-card" style={{
                      background: '#ffffff',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '2px solid #ed8936',
                      borderLeft: '5px solid #ed8936'
                    }}>
                      <h5 style={{ color: '#2d3748', marginBottom: '15px', fontSize: '15px', fontWeight: '700' }}>
                        🥇 Commodities Service
                      </h5>
                      <div style={{ display: 'grid', gap: '12px' }}>
                        <input
                          type="text"
                          value={pageContent[pageKey].commoditiesTitle || ''}
                          onChange={(e) => handleContentChange(pageKey, 'commoditiesTitle', e.target.value)}
                          placeholder="Commodities"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#2d3748'
                          }}
                        />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                          <input
                            type="text"
                            value={pageContent[pageKey].commoditiesPriceLabel || ''}
                            onChange={(e) => handleContentChange(pageKey, 'commoditiesPriceLabel', e.target.value)}
                            placeholder="Spread as low as"
                            style={{
                              width: '100%',
                              padding: '8px 10px',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              fontSize: '13px',
                              color: '#2d3748'
                            }}
                          />
                          <input
                            type="text"
                            value={pageContent[pageKey].commoditiesPriceValue || ''}
                            onChange={(e) => handleContentChange(pageKey, 'commoditiesPriceValue', e.target.value)}
                            placeholder="0.004 pip"
                            style={{
                              width: '100%',
                              padding: '8px 10px',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: '700',
                              color: '#2d3748'
                            }}
                          />
                        </div>
                        <textarea
                          value={pageContent[pageKey].commoditiesDescription || ''}
                          onChange={(e) => handleContentChange(pageKey, 'commoditiesDescription', e.target.value)}
                          placeholder="Get ahead by trading on energy and metals..."
                          rows="3"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '13px',
                            color: '#2d3748',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* CTA Section */}
                  <div className="cta-section" style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <h4 style={{ color: '#2d3748', marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>
                      Call to Action
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px', alignItems: 'end' }}>
                      <div className="form-group">
                        <label style={{
                          display: 'block',
                          marginBottom: '8px',
                          fontWeight: '600',
                          color: '#4a5568',
                          fontSize: '14px'
                        }}>CTA Text</label>
                        <input
                          type="text"
                          value={pageContent[pageKey].servicesCtaText || ''}
                          onChange={(e) => handleContentChange(pageKey, 'servicesCtaText', e.target.value)}
                          placeholder="Start your financial trading journey now."
                          style={{
                            width: '100%',
                            padding: '14px 16px',
                            border: '2px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500',
                            color: '#2d3748'
                          }}
                        />
                      </div>
                      <div className="form-group">
                        <label style={{
                          display: 'block',
                          marginBottom: '8px',
                          fontWeight: '600',
                          color: '#4a5568',
                          fontSize: '14px'
                        }}>Button Text</label>
                        <input
                          type="text"
                          value={pageContent[pageKey].servicesCtaButton || ''}
                          onChange={(e) => handleContentChange(pageKey, 'servicesCtaButton', e.target.value)}
                          placeholder="Create Account"
                          style={{
                            width: '100%',
                            padding: '14px 16px',
                            border: '2px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '15px',
                            fontWeight: '500',
                            color: '#2d3748'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Live Preview Section */}
            <div className="editor-section" style={{
              background: '#f7fafc',
              borderRadius: '12px',
              padding: '25px',
              border: '2px solid #e2e8f0'
            }}>
              <h3 style={{
                color: '#2d3748',
                marginBottom: '20px',
                fontSize: '1.3rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center'
              }}>
                <i className="fas fa-eye" style={{ marginRight: '10px', color: '#3182ce' }}></i>
                Live Preview
              </h3>
              
              <div className="preview-content" style={{
                background: '#ffffff',
                border: '2px solid #e2e8f0',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}>
                <div className="search-preview" style={{
                  marginBottom: '25px',
                  padding: '20px',
                  background: '#f7fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <h4 style={{
                    color: '#2d3748',
                    marginBottom: '15px',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}>🔍 Search Engine Preview:</h4>
                  <div style={{
                    color: '#1a365d',
                    fontSize: '20px',
                    marginBottom: '8px',
                    fontWeight: '500',
                    textDecoration: 'underline',
                    cursor: 'pointer'
                  }}>{pageContent[pageKey].title || 'Page Title'}</div>
                  <div style={{
                    color: '#38a169',
                    fontSize: '14px',
                    marginBottom: '8px',
                    fontWeight: '500'
                  }}>riverprime.com/{pageKey}</div>
                  <div style={{
                    color: '#4a5568',
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}>{pageContent[pageKey].metaDescription || 'Meta description will appear here...'}</div>
                </div>

                <div className="hero-preview" style={{
                  padding: '30px',
                  background: 'linear-gradient(135deg, #2652a0 0%, #1a4086 100%)',
                  borderRadius: '12px',
                  color: '#ffffff',
                  textAlign: 'center'
                }}>
                  <h4 style={{
                    color: '#ffffff',
                    marginBottom: '20px',
                    fontSize: '16px',
                    fontWeight: '600',
                    opacity: '0.9'
                  }}>🎯 Hero Section Preview:</h4>
                  <h1 style={{
                    fontSize: '32px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    color: '#ffffff',
                    lineHeight: '1.2'
                  }}>
                    {pageContent[pageKey].heroTitle || 'Hero Title'}
                    {pageContent[pageKey].heroSubtitle && (
                      <span style={{ color: '#ffdd00', display: 'block', fontSize: '28px', marginTop: '8px' }}>
                        {pageContent[pageKey].heroSubtitle}
                      </span>
                    )}
                  </h1>
                  <p style={{
                    fontSize: '18px',
                    lineHeight: '1.6',
                    margin: '20px 0 0',
                    opacity: '0.9',
                    maxWidth: '600px',
                    marginLeft: 'auto',
                    marginRight: 'auto'
                  }}>{pageContent[pageKey].heroDescription || 'Hero description text will appear here...'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="admin-section">
      <div className="section-header" style={{
        marginBottom: '40px',
        textAlign: 'center',
        padding: '30px'
      }}>
        <h2 style={{
          color: '#1a202c',
          fontSize: '2.5rem',
          fontWeight: '700',
          margin: '0 0 15px',
          textShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>📝 Content Management</h2>
        <p style={{
          color: '#4a5568',
          fontSize: '18px',
          fontWeight: '500',
          margin: '0',
          lineHeight: '1.5'
        }}>Manage page content individually - SEO, meta tags, and hero sections</p>
      </div>

      {/* Navigation Tabs */}
      <div className="content-tabs" style={{
        background: '#ffffff',
        borderRadius: '15px 15px 0 0',
        padding: '0',
        marginBottom: '0',
        border: '2px solid #e2e8f0',
        borderBottom: 'none',
        overflow: 'hidden',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.1)'
      }}>
        <div className="tab-navigation" style={{
          display: 'flex',
          flexWrap: 'wrap',
          borderBottom: '2px solid #f7fafc',
          background: '#f7fafc'
        }}>
          <button
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
            style={{
              background: activeTab === 'overview' ? '#2652a0' : 'transparent',
              color: activeTab === 'overview' ? '#ffffff' : '#4a5568',
              border: 'none',
              padding: '18px 25px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              borderBottom: activeTab === 'overview' ? '4px solid #ffdd00' : '4px solid transparent',
              borderRadius: '8px 8px 0 0'
            }}
          >
            <i className="fas fa-th-large"></i>
            Overview
          </button>
          
          {pages.map((page) => (
            <button
              key={page.key}
              className={`tab-button ${activeTab === page.key ? 'active' : ''}`}
              onClick={() => setActiveTab(page.key)}
              style={{
                background: activeTab === page.key ? '#2652a0' : 'transparent',
                color: activeTab === page.key ? '#ffffff' : '#4a5568',
                border: 'none',
                padding: '18px 22px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                borderBottom: activeTab === page.key ? '4px solid #ffdd00' : '4px solid transparent',
                borderRadius: '8px 8px 0 0'
              }}
            >
              <i className={page.icon}></i>
              {page.title}
            </button>
          ))}
          
          <button
            className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
            style={{
              background: activeTab === 'settings' ? '#2652a0' : 'transparent',
              color: activeTab === 'settings' ? '#ffffff' : '#4a5568',
              border: 'none',
              padding: '18px 25px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              borderBottom: activeTab === 'settings' ? '4px solid #ffdd00' : '4px solid transparent',
              borderRadius: '8px 8px 0 0'
            }}
          >
            <i className="fas fa-cog"></i>
            Site Settings
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content" style={{
        background: '#ffffff',
        borderRadius: '0 0 15px 15px',
        border: '2px solid #e2e8f0',
        borderTop: 'none',
        minHeight: '500px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        {activeTab === 'overview' && (
          <div style={{ padding: '40px' }}>
            <h3 style={{
              color: '#1a202c',
              marginBottom: '30px',
              fontSize: '1.8rem',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center'
            }}>
              <i className="fas fa-th-large" style={{ marginRight: '12px', color: '#3182ce' }}></i>
              Content Overview
            </h3>
            
            <div className="overview-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '25px',
              marginBottom: '40px'
            }}>
              {pages.map((page) => (
                <div key={page.key} className="overview-card" style={{
                  background: '#f7fafc',
                  borderRadius: '12px',
                  padding: '25px',
                  border: '2px solid #e2e8f0',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  ':hover': {
                    borderColor: '#3182ce',
                    boxShadow: '0 8px 25px rgba(49, 130, 206, 0.15)'
                  }
                }} onClick={() => setActiveTab(page.key)}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#3182ce';
                  e.target.style.boxShadow = '0 8px 25px rgba(49, 130, 206, 0.15)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                  e.target.style.transform = 'translateY(0)';
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '18px'
                  }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      background: 'linear-gradient(135deg, #2652a0 0%, #1a4086 100%)',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '15px',
                      boxShadow: '0 4px 8px rgba(38, 82, 160, 0.3)'
                    }}>
                      <i className={page.icon} style={{ 
                        color: '#ffdd00',
                        fontSize: '20px'
                      }}></i>
                    </div>
                    <div>
                      <h4 style={{
                        margin: '0 0 6px',
                        color: '#1a202c',
                        fontSize: '1.2rem',
                        fontWeight: '700'
                      }}>{page.title}</h4>
                      <span style={{
                        color: '#718096',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}>/{page.key}</span>
                    </div>
                  </div>
                  
                  <div style={{
                    fontSize: '14px',
                    color: '#4a5568',
                    marginBottom: '18px',
                    lineHeight: '1.5',
                    fontWeight: '500'
                  }}>{page.description}</div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '13px'
                  }}>
                    <span style={{
                      color: pageContent[page.key].metaDescription.length <= 160 ? '#38a169' : '#e53e3e',
                      fontWeight: '600',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      background: pageContent[page.key].metaDescription.length <= 160 ? '#f0fff4' : '#fed7d7'
                    }}>
                      SEO: {pageContent[page.key].metaDescription.length <= 160 ? '✓ Good' : '✗ Too Long'}
                    </span>
                    <button style={{
                      background: 'linear-gradient(135deg, #3182ce 0%, #2c5282 100%)',
                      color: '#ffffff',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 4px rgba(49, 130, 206, 0.3)'
                    }}>
                      ✏️ Edit Page
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Stats */}
            <div className="content-stats" style={{
              background: '#f7fafc',
              borderRadius: '12px',
              padding: '30px',
              border: '2px solid #e2e8f0',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <h4 style={{
                color: '#1a202c',
                marginBottom: '25px',
                fontSize: '1.4rem',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center'
              }}>
                <i className="fas fa-chart-bar" style={{ marginRight: '10px', color: '#3182ce' }}></i>
                Content Statistics
              </h4>
              <div className="stats-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '25px'
              }}>
                <div className="stat-item" style={{ 
                  textAlign: 'center',
                  background: '#ffffff',
                  padding: '25px',
                  borderRadius: '10px',
                  border: '2px solid #e2e8f0',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: 'bold',
                    color: '#2652a0',
                    marginBottom: '8px'
                  }}>{pages.length}</div>
                  <div style={{
                    color: '#4a5568',
                    fontSize: '15px',
                    fontWeight: '600'
                  }}>Total Pages</div>
                </div>
                <div className="stat-item" style={{ 
                  textAlign: 'center',
                  background: '#ffffff',
                  padding: '25px',
                  borderRadius: '10px',
                  border: '2px solid #e2e8f0',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: 'bold',
                    color: '#38a169',
                    marginBottom: '8px'
                  }}>
                    {Object.values(pageContent).filter(page => 
                      page.metaDescription.length <= 160).length}
                  </div>
                  <div style={{
                    color: '#4a5568',
                    fontSize: '15px',
                    fontWeight: '600'
                  }}>SEO Compliant</div>
                </div>
                <div className="stat-item" style={{ 
                  textAlign: 'center',
                  background: '#ffffff',
                  padding: '25px',
                  borderRadius: '10px',
                  border: '2px solid #e2e8f0',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: 'bold',
                    color: '#ed8936',
                    marginBottom: '8px'
                  }}>
                    {Object.values(pageContent).filter(page => 
                      page.heroTitle && page.heroSubtitle && page.heroDescription).length}
                  </div>
                  <div style={{
                    color: '#4a5568',
                    fontSize: '15px',
                    fontWeight: '600'
                  }}>Complete Heroes</div>
                </div>
                <div className="stat-item" style={{ 
                  textAlign: 'center',
                  background: '#ffffff',
                  padding: '25px',
                  borderRadius: '10px',
                  border: '2px solid #e2e8f0',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: 'bold',
                    color: '#3182ce',
                    marginBottom: '8px'
                  }}>
                    {Math.round(Object.values(pageContent).reduce((avg, page) => 
                      avg + page.metaDescription.length, 0) / pages.length)}
                  </div>
                  <div style={{
                    color: '#4a5568',
                    fontSize: '15px',
                    fontWeight: '600'
                  }}>Avg Meta Length</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div style={{ padding: '40px' }}>
            <div className="content-section" style={{
              background: '#f7fafc',
              borderRadius: '12px',
              padding: '35px',
              marginBottom: '30px',
              border: '2px solid #e2e8f0',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <div className="section-title" style={{
                marginBottom: '30px',
                paddingBottom: '20px',
                borderBottom: '2px solid #e2e8f0'
              }}>
                <h3 style={{
                  color: '#1a202c',
                  margin: '0',
                  fontSize: '1.6rem',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <i className="fas fa-cog" style={{ marginRight: '12px', color: '#3182ce' }}></i>
                  Site Settings
                </h3>
              </div>
              <div className="settings-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                gap: '25px'
              }}>
                <div className="setting-item" style={{ marginBottom: '25px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '10px',
                    fontWeight: '600',
                    color: '#2d3748',
                    fontSize: '15px'
                  }}>Site Name</label>
                  <input
                    type="text"
                    value={siteSettings.siteName}
                    onChange={(e) => handleSettingChange('siteName', e.target.value)}
                    placeholder="RiverPrime"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '500',
                      color: '#2d3748',
                      background: '#ffffff',
                      outline: 'none',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3182ce'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
                
                <div className="setting-item" style={{ marginBottom: '25px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '10px',
                    fontWeight: '600',
                    color: '#2d3748',
                    fontSize: '15px'
                  }}>Tagline</label>
                  <input
                    type="text"
                    value={siteSettings.tagline}
                    onChange={(e) => handleSettingChange('tagline', e.target.value)}
                    placeholder="Your Gateway to Global Markets"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '500',
                      color: '#2d3748',
                      background: '#ffffff',
                      outline: 'none',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3182ce'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
                
                <div className="setting-item full-width" style={{ 
                  marginBottom: '25px',
                  gridColumn: '1 / -1'
                }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '10px',
                    fontWeight: '600',
                    color: '#2d3748',
                    fontSize: '15px'
                  }}>Default Meta Description</label>
                  <textarea
                    value={siteSettings.defaultMetaDescription}
                    onChange={(e) => handleSettingChange('defaultMetaDescription', e.target.value)}
                    placeholder="Default description for SEO..."
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '500',
                      color: '#2d3748',
                      resize: 'vertical',
                      background: '#ffffff',
                      outline: 'none',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      lineHeight: '1.5'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3182ce'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
                
                <div className="setting-item" style={{ marginBottom: '25px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '10px',
                    fontWeight: '600',
                    color: '#2d3748',
                    fontSize: '15px'
                  }}>Contact Email</label>
                  <input
                    type="email"
                    value={siteSettings.contactEmail}
                    onChange={(e) => handleSettingChange('contactEmail', e.target.value)}
                    placeholder="support@riverprime.com"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '500',
                      color: '#2d3748',
                      background: '#ffffff',
                      outline: 'none',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3182ce'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
                
                <div className="setting-item" style={{ marginBottom: '25px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '10px',
                    fontWeight: '600',
                    color: '#2d3748',
                    fontSize: '15px'
                  }}>Contact Phone</label>
                  <input
                    type="text"
                    value={siteSettings.contactPhone}
                    onChange={(e) => handleSettingChange('contactPhone', e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '500',
                      color: '#2d3748',
                      background: '#ffffff',
                      outline: 'none',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3182ce'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
                
                <div className="setting-item full-width" style={{ 
                  marginBottom: '25px',
                  gridColumn: '1 / -1'
                }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '10px',
                    fontWeight: '600',
                    color: '#2d3748',
                    fontSize: '15px'
                  }}>Company Address</label>
                  <input
                    type="text"
                    value={siteSettings.companyAddress}
                    onChange={(e) => handleSettingChange('companyAddress', e.target.value)}
                    placeholder="123 Financial District, Trading City"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '500',
                      color: '#2d3748',
                      background: '#ffffff',
                      outline: 'none',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3182ce'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
                
                <div className="setting-item" style={{ marginBottom: '25px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '10px',
                    fontWeight: '600',
                    color: '#2d3748',
                    fontSize: '15px'
                  }}>Trading Hours</label>
                  <input
                    type="text"
                    value={siteSettings.tradingHours}
                    onChange={(e) => handleSettingChange('tradingHours', e.target.value)}
                    placeholder="Monday to Friday, 24/5 Market Hours"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '500',
                      color: '#2d3748',
                      background: '#ffffff',
                      outline: 'none',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3182ce'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
                
                <div className="setting-item full-width" style={{ 
                  marginBottom: '25px',
                  gridColumn: '1 / -1'
                }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '10px',
                    fontWeight: '600',
                    color: '#2d3748',
                    fontSize: '15px'
                  }}>Risk Warning</label>
                  <textarea
                    value={siteSettings.riskWarning}
                    onChange={(e) => handleSettingChange('riskWarning', e.target.value)}
                    placeholder="Risk warning text..."
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '500',
                      color: '#2d3748',
                      resize: 'vertical',
                      background: '#ffffff',
                      outline: 'none',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      lineHeight: '1.5'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3182ce'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Render individual page editors */}
        {pages.map(page => 
          activeTab === page.key && renderPageEditor(page.key)
        )}
      </div>

      {/* Save Actions */}
      <div className="section-actions" style={{
        textAlign: 'center',
        marginBottom: '40px',
        marginTop: '30px'
      }}>
        <button 
          className="save-btn" 
          onClick={handleSave}
          disabled={!hasChanges}
          style={{
            background: hasChanges 
              ? 'linear-gradient(135deg, #ffdd00 0%, #ffc700 100%)' 
              : '#e2e8f0',
            color: hasChanges ? '#1a202c' : '#a0aec0',
            border: 'none',
            padding: '18px 40px',
            borderRadius: '10px',
            fontSize: '18px',
            fontWeight: '700',
            cursor: hasChanges ? 'pointer' : 'not-allowed',
            transition: 'all 0.3s ease',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: hasChanges ? '0 6px 20px rgba(255, 221, 0, 0.4)' : 'none',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}
        >
          <i className="fas fa-save"></i>
          {hasChanges ? '💾 Save All Changes' : '✅ All Changes Saved'}
        </button>
        
        {hasChanges && (
          <p style={{
            color: '#d69e2e',
            fontSize: '16px',
            margin: '15px 0 0',
            fontStyle: 'italic',
            fontWeight: '500'
          }}>
            ⚠️ You have unsaved changes - click save to apply them to the website
          </p>
        )}
      </div>

      {/* Notification */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default ContentManagement;